import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import * as Yup from "yup";
import ValidateMessages from "../../../../../../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import Form from "../../../../../../../../components/base/form";
import classnames from "classnames";
import {Col} from "reactstrap";
import {OutlinedInputForm} from "../../../../../../../../components/base/input/outlined_input";
import IconButton from "../../../../../../../../components/base/button/icon_button";
import {createUUId, preventEventPropagation} from "../../../../../../../../../core/services/utils/utils";
import {Edit} from "@mui/icons-material";
import SaveOrRollback from "../../../../../../../../components/app-specific/save-or-rollback";
import {UserHomepageControllerContext} from "../../../../../../index";
import {UserHomepageComponentTypes, UserHomepageEventEmitter} from "../../../../../../models";
import {ReactComponent as SlideShowIcon} from "../../../../../../../../../assets/images/user-platform/slide-show.svg";
import {ReactComponent as BannerIcon} from "../../../../../../../../../assets/images/user-platform/banner.svg";
import {ReactComponent as CategoryIcon} from "../../../../../../../../../assets/images/user-platform/category.svg";
import {ReactComponent as SmallProductIcon} from "../../../../../../../../../assets/images/user-platform/small-product.svg";
import {ReactComponent as MediumProductIcon} from "../../../../../../../../../assets/images/user-platform/medium-product.svg";
import {ReactComponent as LargeProductIcon} from "../../../../../../../../../assets/images/user-platform/large-product.svg";
import {ReactComponent as TimeBasedIcon} from "../../../../../../../../../assets/images/user-platform/time-based.svg";
import {ReactComponent as ImageCarouselIcon} from "../../../../../../../../../assets/images/user-platform/image-carousel.svg";
import useIsMounted from "../../../../../../../../hooks/use-is-mounted";
import ObserversService from "../../../../../../../../../core/services/observers";
import debounce from "lodash/debounce";


const schema = Yup.object().shape({
    title: Yup.string().trim().nullable().required(ValidateMessages.required)
});

const validate = makeValidate(schema);
const required = makeRequired(schema);

const minTitleWidth = 50;

const UserHomepageControlPanelComponentHeaderInformation = ({
                                                                currentTitle,
                                                                tabId,
                                                                componentId,
                                                                type,
                                                                inEditMode,
                                                                setInEditMode,
                                                                disabled,
                                                                update,
                                                            }) => {
    const controller = useContext(UserHomepageControllerContext);
    const titleId = useRef(createUUId(true));
    const [width, setWidth] = useState(minTitleWidth);
    const isMounted = useIsMounted();

    const initialValues = useMemo(() => ({
        title: currentTitle
    }), [currentTitle])

    /**
     * With each change in the title of the currentComponent:
     * - updates the width of the title input so that the edit icon can be positioned properly
     */
    useEffect(() => {
        updateWidth([document.getElementById(titleId.current)]);
    }, [initialValues.title])

    /**
     * As soon as the component mounts:
     * - attaches an observer to observe the change in the title input content.
     */
    useEffect(() => {
        const title = document.getElementById(titleId.current);
        if (!title)
            return;
        const throttledCallback = debounce((entries, observer) => updateWidth(entries, observer), 100);
        const observer = ObserversService.newResizeObserver(throttledCallback);
        ObserversService.observeResizeObserver(observer, title)
        return () => {
            ObserversService.disconnectResizeObserver(observer);
        }
    }, [])

    /**
     * Updates the width of the title input so that the edit icon can be positioned properly
     * @param {ResizeObserverEntry[] | Element[]} entries
     */
    const updateWidth = (entries) => {
        requestAnimationFrame(() => {
            if (!isMounted())
                return;
            if (!entries?.length)
                return;
            const entry = entries[0];
            const width = Math.max(entry.contentRect?.width ?? entry.getBoundingClientRect()?.width ?? 0, minTitleWidth);
            setWidth(width);
        })
    }

    /**
     * Exists the edit mode of the component.
     */
    const exitEditMode = () => setInEditMode(false);

    /**
     * Updates the title of the current component in the server.
     *
     * @param values
     * @return {Promise<void>}
     */
    const submitForm = async (values) => {
        if (values.title === initialValues.title) {
            return exitEditMode();
        }
        const resultFlag = await update(values.title);
        if (!isMounted())
            return;
        if (resultFlag) {
            updateComponentTitle(values.title, true);
        }
    }

    /**
     * Changes the component's title without committing it.
     * @param {InputEvent} e
     * @param {formApi} form
     */
    const liveChangeComponentTitle = (e, form) => {
        const newTitle = e.target.value ?? '';
        form.change(e.target.name, newTitle);
        updateComponentTitle(newTitle, false);
    }

    /**
     * Reverts the title changes back to its initial values.
     * @param {string} newTitle
     * @param {formApi} form
     */
    const revertTitleChanges = (newTitle, form) => {
        form.resetFieldState('title');
        if (initialValues.title === newTitle)
            return exitEditMode();
        form.change('title', initialValues.title);
        form.resetFieldState('title');
        updateWidth([document.getElementById(titleId.current)]);
        updateComponentTitle(initialValues.title, true);
    }

    /**
     * Updates the component title in the controller.
     *
     * * if committed, then exits the edit mode.
     * @param {string} title
     * @param {boolean} commit
     */
    const updateComponentTitle = (title, commit = false) => {
        controller.updateComponent(tabId, {
            id: componentId,
            title: title,
        }, commit)
        if (commit) {
            exitEditMode();
        }
    }

    const onEditClicked = (e) => {
        preventEventPropagation(e);
        setInEditMode(true);
        controller.selectComponent(
            tabId,
            componentId,
            UserHomepageEventEmitter.controlPanel,
        )
    }

    const ComponentIcon = useMemo(() => {
        switch (type?.id) {
            case UserHomepageComponentTypes.slideShow:
                return <SlideShowIcon className={'component-icon'}/>
            case UserHomepageComponentTypes.banner:
                return <BannerIcon className={'component-icon'}/>
            case UserHomepageComponentTypes.category:
                return <CategoryIcon className={'component-icon'}/>
            case UserHomepageComponentTypes.smallProduct:
                return <SmallProductIcon className={'component-icon'}/>
            case UserHomepageComponentTypes.mediumProduct:
                return <MediumProductIcon className={'component-icon'}/>
            case UserHomepageComponentTypes.largeProduct:
                return <LargeProductIcon className={'component-icon'}/>
            case UserHomepageComponentTypes.timeBased:
                return <TimeBasedIcon className={'component-icon'}/>
            case UserHomepageComponentTypes.imageCarousel:
                return <ImageCarouselIcon className={'component-icon'}/>
            default:
                return <></>;
        }
    }, [type, type?.id])


    return (
        <>
            <div className={'component-header-information'}>
                <Form
                    className={classnames('user-homepage-input-container', {inEditMode})}
                    onSubmit={submitForm}
                    validate={validate}
                    initialValues={initialValues}
                    render={({values, form}) => {
                        return (
                            <>
                                <Col className={'flex-fill'}>
                                    <p id={titleId.current} className={'invisible'}>
                                        {values.title ?? ''}
                                    </p>
                                    <OutlinedInputForm
                                        style={{
                                            '--width': `${width}px`
                                        }}
                                        fullWidth
                                        disabled={!inEditMode}
                                        placeholder={inEditMode ? 'title...' : ''}
                                        name={'title'}
                                        className={'mr-2'}
                                        required={required.title}
                                        value={values.title ?? ''}
                                        onChange={(e) => liveChangeComponentTitle(e, form)}
                                        InputProps={{
                                            startAdornment: ComponentIcon,
                                            endAdornment: (
                                                !inEditMode &&
                                                <IconButton
                                                    disabled={disabled}
                                                    color={'paper.secondary'}
                                                    onPressed={onEditClicked}
                                                >
                                                    <Edit className={"icon normal"}/>
                                                </IconButton>
                                            ),
                                        }}
                                    />
                                </Col>
                                <Col xs={'auto'}
                                     className={'d-flex align-items-center p-0'}
                                     onClick={preventEventPropagation}
                                >
                                    <SaveOrRollback
                                        show={inEditMode}
                                        loading={disabled}
                                        column={false}
                                        showText={false}
                                        onRollback={() => revertTitleChanges(values.title, form)}
                                        onSave={form.submit}
                                    />
                                </Col>
                            </>
                        )
                    }}
                />
                <div className={classnames('search-type', {'editing-title': inEditMode})}>
                    <p>
                        {type?.title ?? ''}
                    </p>
                </div>
            </div>
        </>
    )
}

export default UserHomepageControlPanelComponentHeaderInformation;
